import React from "react";
import { Box } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { ownerFinanceStatsApi } from "../../../api/ownerFinanceStats";
import OwnerWorkspaceNavigation from "./components/OwnerWorkspaceNavigation";
import OwnerVoiceOperatorPanel from "./voice/OwnerVoiceOperatorPanel";
import { OwnerVoiceOperatorProvider } from "./voice/OwnerVoiceOperatorContext";
import { isAffirmative, isNegative, normalizeVoiceText, parseVoiceDateKey, parseVoiceDateRange, parseVoiceNumber } from "./voice/ownerVoiceParsers";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function localYmd(value = new Date()) {
  const pad = (number) => String(number).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function spokenDuration(minutes) {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  if (!total) return "cero minutos";
  if (total < 60) return `${total} minutos`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return `${hours} ${hours === 1 ? "hora" : "horas"}${rest ? ` y ${rest} minutos` : ""}`;
}

function spokenMoney(value, currency = "ARS") {
  const amount = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(Math.round(Number(value || 0)));
  if (currency === "ARS") return `${amount} pesos`;
  if (currency === "USD") return `${amount} dólares`;
  return `${amount} ${currency}`;
}

function spokenDate(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long", year: "numeric" }).format(date);
}


function stripJarvisMemoryCommand(raw = "") {
  const clean = String(raw || "").trim();
  const stripped = clean.replace(/^\s*(?:jarvis[,:]?\s*)?(?:anotá|anota|anotame|guardá en memoria|guarda en memoria|guardá como recuerdo(?: que)?|guarda como recuerdo(?: que)?|acordate de|recordá(?: que)?|recorda(?: que)?)(?:\s+|$)/i, "").trim();
  return stripped !== clean ? stripped : "";
}

function localNoonIso(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function includesAny(text, phrases = []) {
  return phrases.some((phrase) => text.includes(phrase));
}

function financeCategoryCandidates(options = {}) {
  const byName = new Map();
  const add = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    const key = normalizeVoiceText(clean);
    if (!key || byName.has(key)) return;
    byName.set(key, { name: clean });
  };
  (options.categories || []).filter((row) => !row.kind || row.kind === "expense" || row.kind === "both").forEach((row) => add(row.name));
  (options.mostUsedCategories || []).filter((row) => row.type === "expense").forEach((row) => add(row.category));
  (options.recentCategories || []).filter((row) => row.type === "expense").forEach((row) => add(row.category));
  return [...byName.values()];
}


function incomeCategoryCandidates(options = {}) {
  const byName = new Map();
  const add = (name, row = {}) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    const key = normalizeVoiceText(clean);
    if (!key || byName.has(key)) return;
    byName.set(key, { ...row, name: clean });
  };
  (options.categories || []).filter((row) => !row.kind || row.kind === "income" || row.kind === "both").forEach((row) => add(row.name, row));
  (options.mostUsedCategories || []).filter((row) => row.type === "income").forEach((row) => add(row.category, { kind: "income", uses: row.uses }));
  (options.recentCategories || []).filter((row) => row.type === "income").forEach((row) => add(row.category, { kind: "income" }));
  return [...byName.values()];
}

function numberedCategoryPrompt(rows = []) {
  return rows.slice(0, 5).map((row, index) => `${index + 1}, ${row.name}`).join("; ");
}
function voiceChoiceIndex(raw, length) {
  const value = Math.round(Number(parseVoiceNumber(raw)));
  return value >= 1 && value <= length ? value - 1 : -1;
}

function currencyFromVoice(value = "") {
  const text = normalizeVoiceText(value);
  if (/\b(?:dolar|dolares|usd)\b/.test(text)) return "USD";
  if (/\b(?:euro|euros|eur)\b/.test(text)) return "EUR";
  return "ARS";
}

function totalsForTransactions(rows = []) {
  const totals = {};
  rows.forEach((row) => {
    const currency = String(row.currency || "ARS").toUpperCase();
    totals[currency] = Number(totals[currency] || 0) + Number(row.amount || 0);
  });
  return totals;
}

function spokenTotals(totals = {}) {
  const parts = Object.entries(totals).filter(([, value]) => Number(value) > 0).map(([currency, value]) => spokenMoney(value, currency));
  return parts.length ? parts.join(" y ") : "cero";
}
function spokenAgreementMoney(rows = {}) {
  const parts = Object.entries(rows || {}).map(([currency, value]) => [currency, Number((value?.amount ?? value) || 0)]).filter(([, value]) => value > 0).map(([currency, value]) => spokenMoney(value, currency));
  return parts.length ? parts.join(" y ") : "cero";
}

function financeRangeLabel(range) {
  if (!range) return "en todo el histórico";
  if (range.label && !/^\d{4}-\d{2}-\d{2}$/.test(range.label)) return `en ${range.label}`;
  if (range.from === range.to) return `el ${spokenDate(range.from)}`;
  return `entre ${spokenDate(range.from)} y ${spokenDate(range.to)}`;
}

function financeHistoryBucket(range) {
  if (!range) return "month";
  const start = new Date(`${range.from}T12:00:00`);
  const end = new Date(`${range.to}T12:00:00`);
  const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
  if (days <= 45) return "day";
  if (days <= 150) return "week";
  return "month";
}

function navigationQuery(value = "") {
  return normalizeVoiceText(value)
    .replace(/\b(?:jarvis|abri|abrime|abrir|mostra|mostrame|mostrar|ver|anda|andame|llevame|lleva|ir|quiero|la|el|una|un|ficha|detalle|de|del|al|a|cliente|clientes|persona|personas|proyecto|proyectos|frente|iniciativa|rutina|rutinas|habito|habitos|dia|fecha)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function entityScore(row, query, fields = ["name"]) {
  const q = normalizeVoiceText(query);
  if (!q) return -1;
  let best = -1;
  fields.forEach((field) => {
    const value = normalizeVoiceText(row?.[field] || "");
    if (!value) return;
    if (value === q) best = Math.max(best, 120);
    else if (value.startsWith(q)) best = Math.max(best, 105);
    else if (value.includes(q)) best = Math.max(best, 92);
    else if (q.includes(value) && value.length >= 4) best = Math.max(best, 84);
    const words = q.split(" ").filter((word) => word.length >= 2);
    const common = words.filter((word) => value.includes(word)).length;
    if (common) best = Math.max(best, 45 + common * 12);
  });
  return best;
}

function chooseEntity(rows, query, fields = ["name"]) {
  const ranked = (rows || [])
    .map((row) => ({ row, score: entityScore(row, query, fields) }))
    .filter((item) => item.score >= 55)
    .sort((a, b) => b.score - a.score);
  if (!ranked.length) return { row: null, alternatives: [] };
  const ambiguous = ranked.length > 1 && ranked[0].score < 120 && ranked[1].score >= ranked[0].score - 5;
  return { row: ambiguous ? null : ranked[0].row, alternatives: ambiguous ? ranked.slice(0, 3).map((item) => item.row) : [] };
}

function memorySpeech(memories, range) {
  const summary = memories?.summary || {};
  const items = memories?.items || [];
  if (!items.length) return `Entre ${spokenDate(range.from)} y ${spokenDate(range.to)} no encontré actividad guardada todavía.`;
  const parts = [`Entre ${spokenDate(range.from)} y ${spokenDate(range.to)} encontré ${items.length} registros.`];
  if (summary.workMinutes) parts.push(`Trabajaste ${spokenDuration(summary.workMinutes)}.`);
  if (summary.completedCommitments) parts.push(`Completaste ${summary.completedCommitments} tareas.`);
  if (summary.completedRoutines) parts.push(`Cumpliste ${summary.completedRoutines} rutinas.`);
  if (summary.interactions) parts.push(`Registraste ${summary.interactions} contactos.`);
  const highlights = [...items].reverse().map((item) => String(item.title || "").trim()).filter(Boolean).slice(0, 4);
  if (highlights.length) parts.push(`Recuerdo: ${highlights.join("; ")}.`);
  return parts.join(" ");
}

function actionPath(action) {
  return `/dashboard/owner?jarvisAction=${encodeURIComponent(action)}`;
}

export default function OwnerJarvisWorkspace() {
  const navigate = useNavigate();
  const location = useLocation();
  const snapshotRef = React.useRef(null);
  const financeProjectFlowRef = React.useRef(null);

  const refreshSnapshot = React.useCallback(async () => {
    const [centerResponse, assistantResponse, calendarResponse] = await Promise.all([
      ownerApi.center(),
      ownerApi.assistant({ currency: "ARS" }),
      ownerApi.calendar({ date: localYmd() }),
    ]);
    const snapshot = {
      center: centerResponse?.center || null,
      assistant: assistantResponse?.assistant || null,
      calendar: calendarResponse?.calendar || null,
      capturedAt: Date.now(),
    };
    snapshotRef.current = snapshot;
    return snapshot;
  }, []);

  const currentSnapshot = React.useCallback(async ({ force = false } = {}) => {
    const cached = snapshotRef.current;
    if (!force && cached && Date.now() - cached.capturedAt < 90000) return cached;
    return refreshSnapshot();
  }, [refreshSnapshot]);

  React.useEffect(() => {
    refreshSnapshot().catch(() => undefined);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refreshSnapshot().catch(() => undefined);
    }, 90000);
    return () => window.clearInterval(timer);
  }, [refreshSnapshot]);

  const voiceActions = React.useMemo(() => ({
    finance: { label: "movimiento", formId: "finance", aliases: ["movimiento", "gasto", "ingreso", "transferencia", "registrar plata", "registrar dinero", "compra"], ack: "Registrar movimiento.", run: () => navigate(actionPath("finance")) },
    consumption: { label: "consumo", formId: "finance", aliases: ["consumo", "consumir stock", "usar stock", "descontar stock"], ack: "Registrar consumo de stock.", run: () => navigate(actionPath("consumption")) },
    client: { label: "persona", formId: "client", resetContext: true, aliases: ["persona nueva", "cliente nuevo", "prospecto nuevo", "nuevo cliente", "nueva persona", "cargar cliente"], ack: "Nueva persona.", run: () => navigate(actionPath("client")) },
    proposal: { label: "propuesta", formId: "proposal", aliases: ["crear propuesta", "nueva propuesta", "presupuesto", "cotizacion", "venta posible"], ack: "Nueva propuesta.", run: () => navigate(actionPath("proposal")) },
    commitment: { label: "tarea", formId: "commitment", aliases: ["crear tarea", "nueva tarea", "seguimiento", "recordatorio", "turno", "agendar", "pendiente"], ack: "Nuevo seguimiento.", run: () => navigate(actionPath("commitment")) },
    routine: { label: "rutina", formId: "routine", aliases: ["crear rutina", "nueva rutina", "crear habito", "nuevo habito"], ack: "Nueva rutina.", run: () => navigate(actionPath("routine")) },
    worklog: { label: "registrar trabajo", formId: "worklog", aliases: ["registrar lo que hice", "registrar trabajo", "cargar lo que hice", "trabajo real"], ack: "Contame qué hiciste.", run: () => navigate(actionPath("worklog")) },
    recurring: { label: "recurrente", formId: "recurring", aliases: ["crear recurrente", "acuerdo recurrente", "venta mensual", "servicio mensual", "cobro mensual"], ack: "Nuevo acuerdo recurrente.", run: () => navigate(actionPath("recurring")) },
    interaction: { label: "nota", formId: "interaction", aliases: ["registrar contacto", "anotacion de cliente", "conversacion con cliente"], ack: "Registrar contacto.", run: () => navigate(actionPath("interaction")) },
    initiative: { label: "proyecto", formId: "initiative", aliases: ["crear proyecto", "nuevo proyecto", "crear iniciativa"], ack: "Nuevo proyecto.", run: () => navigate(actionPath("initiative")) },
    import: { label: "importar", formId: "import", aliases: ["importar movimientos", "importar gastos", "importar archivo"], ack: "Abro la importación.", run: () => navigate(actionPath("import")) },
    today: { label: "hoy", route: "/dashboard/owner", aliases: ["inicio", "panel de hoy", "volver a hoy", "mi dia", "mi día"], contexts: ["hoy", "owner"], ack: "Voy a Hoy.", run: () => navigate("/dashboard/owner") },
    calendar: { label: "calendario", route: "/dashboard/owner/calendario", aliases: ["calendario", "agenda", "ver agenda", "ver calendario"], contexts: ["calendario"], ack: "Voy al calendario.", run: () => navigate("/dashboard/owner/calendario") },
    clients: { label: "clientes", route: "/dashboard/owner/clientes", aliases: ["ver clientes", "panel de clientes", "crm"], contexts: ["clientes"], ack: "Voy a clientes.", run: () => navigate("/dashboard/owner/clientes") },
    projectsPage: { label: "proyectos", route: "/dashboard/owner/proyectos", aliases: ["ver proyectos", "mis proyectos", "panel de proyectos", "proyectos 360"], contexts: ["proyectos"], ack: "Voy a proyectos.", run: () => navigate("/dashboard/owner/proyectos") },
    movements: { label: "movimientos", route: "/dashboard/owner/movimientos", aliases: ["ver movimientos", "tabla de movimientos"], contexts: ["movimientos", "finanzas"], ack: "Voy a movimientos.", run: () => navigate("/dashboard/owner/movimientos") },
    operation: { label: "operación", route: "/dashboard/owner/operacion", aliases: ["operacion", "ver operacion", "operativa"], contexts: ["operacion"], ack: "Voy a Operación.", run: () => navigate("/dashboard/owner/operacion") },
    account: { label: "caja", aliases: ["crear caja", "nueva caja", "crear cuenta", "nueva cuenta", "nuevo banco", "nueva billetera"], contexts: ["movimientos", "finanzas"], ack: "Nueva caja o cuenta.", run: () => navigate("/dashboard/owner/movimientos?jarvisAction=account") },
    category: { label: "categoría", aliases: ["crear categoria", "nueva categoria"], contexts: ["movimientos", "estadisticas", "finanzas"], ack: "Nueva categoría.", run: () => navigate("/dashboard/owner/movimientos?jarvisAction=category") },
    financePage: { label: "finanzas", route: "/dashboard/owner/finanzas", aliases: ["ver finanzas", "mis finanzas"], contexts: ["finanzas", "movimientos"], ack: "Voy a finanzas.", run: () => navigate("/dashboard/owner/finanzas") },
    stats: { label: "estadísticas", route: "/dashboard/owner/estadisticas", aliases: ["estadisticas", "estadistica", "futuro", "historico"], contexts: ["estadisticas", "finanzas"], ack: "Voy a estadísticas.", run: () => navigate("/dashboard/owner/estadisticas") },
  }), [navigate]);

  const briefing = React.useMemo(() => {
    const now = new Date();
    const date = new Intl.DateTimeFormat("es-AR", { weekday: "long", day: "numeric", month: "long" }).format(now);
    const time = new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(now);
    return `Jarvis activo. Hoy es ${date} y son las ${time}. Estoy conectado a Owner 360. No voy a inventarte tareas: primero revisamos decisiones de proyectos y después seguimos obligaciones reales.`;
  }, []);

  const handleUnhandled = React.useCallback(async ({ raw, text }) => {
    const memory = stripJarvisMemoryCommand(raw);
    if (memory) {
      const memoryRange = parseVoiceDateRange(raw);
      await ownerApi.createLifeEntry({
        text: memory,
        kind: "note",
        inputSource: "voice",
        occurredAt: memoryRange ? localNoonIso(memoryRange.from) : new Date().toISOString(),
        structuredData: {
          jarvis: true,
          captureType: "memory",
          capturedFrom: "owner360-loop",
          ...(memoryRange ? { memoryRange: { from: memoryRange.from, to: memoryRange.to } } : {}),
        },
      });
      snapshotRef.current = null;
      return { handled: true, message: memoryRange ? `Listo. Guardé ese recuerdo entre ${spokenDate(memoryRange.from)} y ${spokenDate(memoryRange.to)}.` : `Listo. Guardé: ${memory}.` };
    }

    const financeFlow = financeProjectFlowRef.current;
    const reviewIncomeCategory = async (category, range = null) => {
      const response = await ownerApi.listFinanceTransactions({ limit: 500, type: "income", category, from: range?.from || "", to: range?.to || "" });
      const rows = response?.transactions || [];
      const ids = rows.map(idOf).filter(Boolean);
      const totals = totalsForTransactions(rows);
      if (!rows.length) return { handled: true, message: `No encontré entradas de dinero en ${category}${range ? ` ${financeRangeLabel(range)}` : ""}.` };
      const params = new URLSearchParams();
      params.set("tipo", "income"); params.set("categoria", category); params.set("jarvisReview", "1");
      if (range?.from) params.set("desde", range.from); if (range?.to) params.set("hasta", range.to);
      navigate(`/dashboard/owner/movimientos?${params.toString()}`);
      financeProjectFlowRef.current = { stage: "review", category, transactionIds: ids, totals, firstOccurredAt: rows[rows.length - 1]?.occurredAt || null, lastOccurredAt: rows[0]?.occurredAt || null };
      return { handled: true, message: `Encontré ${rows.length} entradas en ${category} por ${spokenTotals(totals)}. Te las muestro juntas. ¿Qué querés hacer con este lote? Por ejemplo, podés decir vinculalas a un proyecto.` };
    };

    if (financeFlow?.stage === "category-choice") {
      if (includesAny(text, ["cancelar", "dejalo", "ninguna"])) { financeProjectFlowRef.current = null; return { handled: true, message: "Listo, no toqué ninguna categoría." }; }
      const index = voiceChoiceIndex(raw, financeFlow.options?.length || 0);
      if (index < 0) return { handled: true, message: `Decime el número de la categoría que querés corregir: ${numberedCategoryPrompt(financeFlow.options)}.` };
      const selected = financeFlow.options[index];
      if (includesAny(text, ["usar", "mostrar", "ver", "esa esta bien"])) {
        financeProjectFlowRef.current = null;
        return reviewIncomeCategory(selected.name, financeFlow.range);
      }
      financeProjectFlowRef.current = { ...financeFlow, stage: "category-rename", selected };
      return { handled: true, message: `Elegiste ${selected.name}. ¿Cómo querés que se llame? Decime solamente el nombre nuevo.` };
    }

    if (financeFlow?.stage === "category-rename") {
      const nextName = String(raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 80);
      if (!nextName || nextName.length < 2) return { handled: true, message: "No tomé bien el nombre. Repetímelo como querés que quede escrito." };
      const selected = financeFlow.selected || {};
      if (idOf(selected)) await ownerApi.updateFinanceCategory(idOf(selected), { name: nextName, kind: selected.kind || "income", renameHistory: true });
      else await ownerApi.createFinanceCategory({ name: nextName, kind: "income", renameFrom: selected.name });
      financeProjectFlowRef.current = null;
      const result = await reviewIncomeCategory(nextName, financeFlow.range);
      return { ...result, message: `Listo. Renombré ${selected.name} como ${nextName} y retomé lo que estábamos haciendo. ${result.message}` };
    }

    if (financeFlow?.stage === "review") {
      if (includesAny(text, ["linkear", "vincular", "asociar"]) && /\b(?:proyecto|iniciativa|frente)\b/.test(text)) {
        financeProjectFlowRef.current = { ...financeFlow, stage: "project-exists" };
        return { handled: true, message: "Perfecto. ¿Ese proyecto ya está creado? Decime sí y el nombre, o no todavía." };
      }
      if (includesAny(text, ["cancelar", "dejalo", "nada"])) { financeProjectFlowRef.current = null; return { handled: true, message: "Listo. Dejé esos cobros solamente filtrados, sin cambiar ninguna relación." }; }
    }

    if (financeFlow?.stage === "project-exists") {
      if (isNegative(raw) || includesAny(text, ["no esta creado", "no existe", "todavia no"])) {
        financeProjectFlowRef.current = { ...financeFlow, stage: "new-project-name" };
        return { handled: true, message: "Bien. ¿Cómo querés llamar al proyecto?" };
      }
      if (isAffirmative(raw) || /\b(?:se llama|proyecto)\b/.test(text)) {
        const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
        const query = normalizeVoiceText(raw).replace(/\b(?:si|sí|ya|esta|está|creado|existe|se llama|proyecto|el|la)\b/g, " ").replace(/\s+/g, " ").trim();
        const result = chooseEntity(response?.initiatives || [], query, ["name", "outcome", "notes"]);
        if (result.row) {
          const initiativeId = idOf(result.row);
          await ownerApi.bulkLinkProjectPayments({ transactionIds: financeFlow.transactionIds, initiativeId });
          const agreementResponse = await ownerApi.projectAgreement(initiativeId);
          const baseTerm = (agreementResponse?.agreement?.terms || []).find((row) => row.kind === "base" && row.status !== "cancelled");
          financeProjectFlowRef.current = baseTerm
            ? { ...financeFlow, stage: "payment-attribution", initiativeId, initiativeName: result.row.name, baseTermId: idOf(baseTerm), baseTermTitle: baseTerm.title }
            : { ...financeFlow, stage: "contract-question", initiativeId, initiativeName: result.row.name };
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
          return { handled: true, message: baseTerm
            ? `Listo. Vinculé ${financeFlow.transactionIds.length} cobros a ${result.row.name}. Este proyecto ya tiene ${baseTerm.title || "contrato base"}. ¿Estos cobros corresponden al contrato base o a un bloque o término nuevo?`
            : `Listo. Vinculé ${financeFlow.transactionIds.length} cobros a ${result.row.name} sin tocar sus montos ni fechas. ¿Querés que configuremos ahora el contrato base que explica esos pagos?` };
        }
        if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
        financeProjectFlowRef.current = { ...financeFlow, stage: "existing-project-name" };
        return { handled: true, message: "No lo pude identificar. Decime solamente el nombre del proyecto como aparece en Owner 360." };
      }
      return { handled: true, message: "Decime si el proyecto ya existe. Si existe, decime el nombre. Si no, decime no todavía." };
    }

    if (financeFlow?.stage === "existing-project-name") {
      const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
      const result = chooseEntity(response?.initiatives || [], raw, ["name", "outcome", "notes"]);
      if (result.row) {
        const initiativeId = idOf(result.row);
        await ownerApi.bulkLinkProjectPayments({ transactionIds: financeFlow.transactionIds, initiativeId });
        const agreementResponse = await ownerApi.projectAgreement(initiativeId);
        const baseTerm = (agreementResponse?.agreement?.terms || []).find((row) => row.kind === "base" && row.status !== "cancelled");
        financeProjectFlowRef.current = baseTerm
          ? { ...financeFlow, stage: "payment-attribution", initiativeId, initiativeName: result.row.name, baseTermId: idOf(baseTerm), baseTermTitle: baseTerm.title }
          : { ...financeFlow, stage: "contract-question", initiativeId, initiativeName: result.row.name };
        navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
        return { handled: true, message: baseTerm
          ? `Vinculé los cobros a ${result.row.name}. ¿Corresponden a ${baseTerm.title || "el contrato base"} o a un bloque nuevo?`
          : `Vinculé los cobros a ${result.row.name}. ¿Querés configurar el contrato base ahora?` };
      }
      return { handled: true, message: "No encontré ese proyecto. Repetime el nombre o decime no está creado." };
    }

    if (financeFlow?.stage === "new-project-name") {
      const name = String(raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
      if (!name || normalizeVoiceText(name).split(" ").length > 18) return { handled: true, message: "Decime un nombre corto para el proyecto." };
      const response = await ownerApi.createInitiative({
        name, domain: "freelance", status: "active", priority: "normal",
        outcome: `Proyecto reconstruido desde ${financeFlow.transactionIds.length} cobros históricos de ${financeFlow.category}.`,
        startedAt: financeFlow.firstOccurredAt || undefined,
      });
      const initiative = response?.initiative;
      const initiativeId = idOf(initiative);
      await ownerApi.bulkLinkProjectPayments({ transactionIds: financeFlow.transactionIds, initiativeId });
      financeProjectFlowRef.current = { ...financeFlow, stage: "contract-question", initiativeId, initiativeName: initiative?.name || name };
      navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
      return { handled: true, message: `Creé ${initiative?.name || name} y vinculé ${financeFlow.transactionIds.length} cobros históricos. ¿Querés que armemos ahora el contrato base que explica esos pagos?` };
    }

    if (financeFlow?.stage === "payment-attribution") {
      if (includesAny(text, ["contrato base", "base", "contrato existente", "lo de siempre"])) {
        await ownerApi.bulkLinkProjectPayments({ transactionIds: financeFlow.transactionIds, initiativeId: financeFlow.initiativeId, projectTermId: financeFlow.baseTermId });
        financeProjectFlowRef.current = null;
        return { handled: true, message: `Perfecto. Estos ${financeFlow.transactionIds.length} cobros quedaron atribuidos a ${financeFlow.baseTermTitle || "el contrato base"}. La ficha del proyecto ya los muestra dentro de ese acuerdo.` };
      }
      if (includesAny(text, ["nuevo bloque", "bloque nuevo", "nuevo termino", "termino nuevo", "ampliacion", "aumento"])) {
        financeProjectFlowRef.current = { ...financeFlow, stage: "term-title", attributeSelectionToNewTerm: true };
        return { handled: true, message: "Bien. ¿Cómo querés llamar a ese nuevo bloque o término?" };
      }
      return { handled: true, message: "Decime contrato base si son pagos del acuerdo existente, o nuevo bloque si corresponden a una ampliación." };
    }

    if (financeFlow?.stage === "contract-question") {
      if (isNegative(raw)) { financeProjectFlowRef.current = null; return { handled: true, message: "Perfecto. Los cobros ya quedaron vinculados al proyecto. El contrato lo podemos completar después desde la ficha." }; }
      if (isAffirmative(raw) || includesAny(text, ["configurar", "armar contrato", "contrato base"])) {
        financeProjectFlowRef.current = { ...financeFlow, stage: "contract-mode" };
        return { handled: true, message: "¿El acuerdo base era mensual, por un monto fijo o en cuotas?" };
      }
    }

    if (financeFlow?.stage === "contract-mode") {
      let billingMode = "";
      if (/\b(?:mensual|mes|mensualidad)\b/.test(text)) billingMode = "monthly";
      else if (/\b(?:cuota|cuotas)\b/.test(text)) billingMode = "installments";
      else if (/\b(?:fijo|unico|único|total)\b/.test(text)) billingMode = "one_time";
      if (!billingMode) return { handled: true, message: "Decime mensual, monto fijo o cuotas." };
      financeProjectFlowRef.current = { ...financeFlow, stage: "contract-amount", billingMode };
      return { handled: true, message: billingMode === "monthly" ? "¿Cuánto era el monto acordado por mes?" : "¿Cuál era el monto acordado?" };
    }

    if (financeFlow?.stage === "contract-amount") {
      const amount = parseVoiceNumber(raw);
      if (!(amount > 0)) return { handled: true, message: "No pude tomar el monto. Decímelo de nuevo, por ejemplo quinientos mil pesos." };
      const currency = currencyFromVoice(raw);
      const response = await ownerApi.createProjectTerm(financeFlow.initiativeId, {
        kind: "base", title: "Contrato base", status: "accepted", amount, currency, billingMode: financeFlow.billingMode,
        effectiveFrom: financeFlow.firstOccurredAt || undefined, assignUnassignedPayments: true, source: "voice",
        notes: `Contrato base reconstruido con Jarvis a partir de cobros históricos de ${financeFlow.category}.`,
      });
      const summary = response?.agreement?.summary || {};
      financeProjectFlowRef.current = { ...financeFlow, stage: "more-terms" };
      return { handled: true, message: `Listo. Guardé el contrato base por ${spokenMoney(amount, currency)}${financeFlow.billingMode === "monthly" ? " mensuales" : ""} y sincronicé ${summary.paymentCount || financeFlow.transactionIds.length} cobros del proyecto. ¿Hubo después algún aumento, término nuevo o bloque adicional?` };
    }

    if (financeFlow?.stage === "more-terms") {
      if (isNegative(raw)) { financeProjectFlowRef.current = null; return { handled: true, message: "Perfecto. El proyecto ya tiene contrato base y pagos históricos sincronizados. Podemos agregar nuevos términos cuando aparezcan." }; }
      if (isAffirmative(raw) || includesAny(text, ["aumento", "nuevo termino", "nuevo bloque", "ampliacion"])) {
        financeProjectFlowRef.current = { ...financeFlow, stage: "term-title" };
        return { handled: true, message: "¿Cómo querés llamar a ese nuevo bloque o término?" };
      }
    }

    if (financeFlow?.stage === "term-title") {
      const termTitle = String(raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
      if (!termTitle) return { handled: true, message: "Decime el nombre del bloque." };
      financeProjectFlowRef.current = { ...financeFlow, stage: "term-amount", termTitle };
      return { handled: true, message: "¿Cuánto agrega ese bloque o término al acuerdo?" };
    }

    if (financeFlow?.stage === "term-amount") {
      const amount = parseVoiceNumber(raw);
      if (!(amount >= 0)) return { handled: true, message: "Repetíme el monto del nuevo término." };
      const currency = currencyFromVoice(raw);
      financeProjectFlowRef.current = { ...financeFlow, stage: "term-task", termAmount: amount, termCurrency: currency };
      return { handled: true, message: `Anoto ${spokenMoney(amount, currency)}. ¿Querés que además cree una tarea vinculada a este bloque? Si sí, decime el título de la tarea. Si no, decime no.` };
    }

    if (financeFlow?.stage === "term-task") {
      const taskTitle = isNegative(raw) ? "" : String(raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
      await ownerApi.createProjectTerm(financeFlow.initiativeId, {
        kind: "block", title: financeFlow.termTitle, status: "accepted", amount: financeFlow.termAmount, currency: financeFlow.termCurrency, billingMode: "one_time", source: "voice", taskTitle,
        ...(financeFlow.attributeSelectionToNewTerm ? { transactionIds: financeFlow.transactionIds } : {}),
      });
      financeProjectFlowRef.current = null;
      navigate(`/dashboard/owner/proyectos/${encodeURIComponent(financeFlow.initiativeId)}`);
      return { handled: true, message: `Listo. Agregué ${financeFlow.termTitle} por ${spokenMoney(financeFlow.termAmount, financeFlow.termCurrency)}${taskTitle ? ` y creé la tarea ${taskTitle}` : ""}. Todo quedó dentro del mismo proyecto.` };
    }

    const asksIncomeCategory = /\b(?:mostra|mostrame|mostrar|ver)\b/.test(text)
      && /\b(?:entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros)\b/.test(text);
    if (asksIncomeCategory) {
      const options = await ownerApi.financeMovementOptions();
      const categories = incomeCategoryCandidates(options);
      const result = chooseEntity(categories, text, ["name"]);
      const range = parseVoiceDateRange(raw);
      if (result.row) return reviewIncomeCategory(result.row.name, range);
      const fallback = (result.alternatives?.length ? result.alternatives : categories).slice(0, 5);
      if (fallback.length) {
        financeProjectFlowRef.current = { stage: "category-choice", options: fallback, range, originalCommand: raw };
        return { handled: true, message: `No encontré esa categoría exactamente. Si querés corregir una de las que ya existen, elegí por número: ${numberedCategoryPrompt(fallback)}. Decime el número para renombrarla, o por ejemplo usar 2 si sólo querés revisar esa.` };
      }
      return { handled: true, message: "No encontré categorías de ingreso todavía. Podés decir crear categoría y la armamos sin perder el resto de Owner 360." };
    }

    const asksProjectControl = /\b(?:como va|cuanto|cuanta|pagado|pago|pagaron|presupuesto|falta cobrar|falta pagar|pendiente)\b/.test(text)
      && /\b(?:proyecto|me pago|me pagaron|cliente|presupuesto|falta cobrar|como va)\b/.test(text);
    if (asksProjectControl) {
      const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
      const result = chooseEntity(response?.initiatives || [], text, ["name", "outcome", "notes"]);
      if (result.row) {
        const initiativeId = idOf(result.row);
        const [agreementResponse, productResponse] = await Promise.all([
          ownerApi.projectAgreement(initiativeId),
          ownerApi.initiativeProductHealth(initiativeId, { days: 30 }).catch(() => ({ health: null })),
        ]);
        const agreement = agreementResponse?.agreement || {};
        const summary = agreement.summary || {};
        const health = productResponse?.health || null;
        navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
        const parts = [`${result.row.name}: cobrado ${spokenAgreementMoney(summary.paidByCurrency)}.`];
        if (Object.keys(summary.knownBudgetByCurrency || {}).length) parts.push(`Presupuesto cerrado conocido ${spokenAgreementMoney(summary.knownBudgetByCurrency)}; pendiente ${spokenAgreementMoney(summary.outstandingByCurrency)}.`);
        const usdTerm = (agreement.terms || []).find((term) => term.currency === "USD" && term.outstanding > 0 && term.fx?.outstandingArsNow);
        if (usdTerm) parts.push(`Del saldo en dólares faltan ${spokenMoney(usdTerm.outstanding, "USD")}; con la cotización ${usdTerm.fx.quote || "configurada"} de hoy son aproximadamente ${spokenMoney(usdTerm.fx.outstandingArsNow, "ARS")}. Lo que ya cobraste queda congelado y no se recalcula.`);
        if (Object.keys(summary.recurringMonthlyByCurrency || {}).length) parts.push(`Además tiene ${spokenAgreementMoney(summary.recurringMonthlyByCurrency)} mensuales recurrentes.`);
        if (summary.taskProgress?.total) parts.push(`Checklist ${summary.taskProgress.done} de ${summary.taskProgress.total} tareas completas.`);
        if (health?.linked) {
          const stageLabels = { implementation: "en implementación", delivered: "entregado", operating: "operando", inactive: "inactivo" };
          parts.push(`El WebProfit está ${stageLabels[health.adoption?.stage] || "vinculado"}, con adopción ${Math.round(Number(health.adoption?.score || 0))} por ciento.`);
          parts.push(`En los últimos ${health.period?.days || 30} días tuvo ${health.storefront?.pageViews || 0} visitas, ${health.storefront?.contactClicks || 0} contactos y ${health.storefront?.cartAdds || 0} agregados al carrito.`);
        }
        parts.push("Te abro la ficha para ver contrato, avance y uso real del producto.");
        return { handled: true, message: parts.join(" ") };
      }
      if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime el nombre más completo.` };
    }

    const asksWebProfitProduct = /\b(?:webprofit|pagina|web|visitas|visitantes|contactos|whatsapp|carrito|carritos|checkout|uso|adopcion|ultimo uso|ultima edicion)\b/.test(text)
      && /\b(?:proyecto|cliente|webprofit|pagina|visitas|contactos|carritos|uso|adopcion|como va|estado)\b/.test(text);
    if (asksWebProfitProduct) {
      const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
      const result = chooseEntity(response?.initiatives || [], text, ["name", "outcome", "notes"]);
      if (result.row) {
        const initiativeId = idOf(result.row);
        const productResponse = await ownerApi.initiativeProductHealth(initiativeId, { days: 30 });
        const health = productResponse?.health || null;
        navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
        if (!health?.linked) return { handled: true, message: `${result.row.name} todavía no tiene un Project WebProfit vinculado. Te abro la ficha para vincularlo.` };
        const stageLabels = { implementation: "en implementación", delivered: "entregado", operating: "operando", inactive: "inactivo" };
        const parts = [`${result.row.name}: WebProfit ${stageLabels[health.adoption?.stage] || "vinculado"}, adopción ${Math.round(Number(health.adoption?.score || 0))} por ciento.`];
        parts.push(`Últimos ${health.period?.days || 30} días: ${health.storefront?.pageViews || 0} visitas, ${health.storefront?.contactClicks || 0} contactos, ${health.storefront?.cartAdds || 0} carritos y ${health.orders?.orders || 0} pedidos.`);
        if (health.panel?.landingEdits) parts.push(`La página se guardó ${health.panel.landingEdits} veces en ese período.`);
        parts.push("Te abro la ficha con la evidencia.");
        return { handled: true, message: parts.join(" ") };
      }
      if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime el nombre más completo.` };
    }

    if (includesAny(text, ["ver proyectos", "abrir proyectos", "ir a proyectos", "panel de proyectos", "mis proyectos"])) {
      navigate("/dashboard/owner/proyectos");
      return { handled: true, message: "Abro Proyectos." };
    }

    if (includesAny(text, ["ver movimientos", "abrir movimientos", "ir a movimientos", "tabla de movimientos"])) {
      navigate("/dashboard/owner/movimientos");
      return { handled: true, message: "Abro Movimientos." };
    }
    if (includesAny(text, ["ver finanzas", "abrir finanzas", "ir a finanzas", "mis finanzas"])) {
      navigate("/dashboard/owner/finanzas");
      return { handled: true, message: "Abro Finanzas." };
    }
    if (includesAny(text, ["ir a hoy", "abrir hoy", "volver a hoy", "panel de hoy"])) {
      navigate("/dashboard/owner");
      return { handled: true, message: "Vuelvo a Hoy." };
    }

    const asksAccountBalance = /\b(?:cuanto|cuanta|saldo)\b/.test(text)
      && /\b(?:plata|saldo|deberia tener|debo tener|tengo en|hay en)\b/.test(text)
      && !/\b(?:gaste|gastado|gasto|pague|pagado|pago)\b/.test(text);
    if (asksAccountBalance) {
      const response = await ownerApi.listFinanceAccounts();
      const accounts = response?.accounts || [];
      const result = chooseEntity(accounts, text, ["name", "type", "currency"]);
      if (result.row) {
        const account = result.row;
        const accountId = idOf(account);
        navigate(`/dashboard/owner/movimientos?cuenta=${encodeURIComponent(accountId)}`);
        return {
          handled: true,
          message: `En ${account.name} deberías tener ${spokenMoney(account.balance, account.currency || "ARS")}. Te abro la caja para que veas saldo inicial y los movimientos que explican ese saldo.`,
        };
      }
      if (result.alternatives?.length) {
        return { handled: true, message: `Encontré varias cajas parecidas: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
      }
      return { handled: true, message: "No encontré esa caja. Decime el nombre como aparece en Mi plata." };
    }

    const asksExpenseByCategory = /\b(?:cuanto|cuanta|cuantos)\b/.test(text)
      && /\b(?:gaste|gastado|gasto|pague|pagado|pago|pagar|se fue)\b/.test(text);
    if (asksExpenseByCategory) {
      const [optionsResponse, accountsResponse] = await Promise.all([
        ownerApi.financeMovementOptions(),
        ownerApi.listFinanceAccounts(),
      ]);
      const categories = financeCategoryCandidates(optionsResponse);
      const result = chooseEntity(categories, text, ["name"]);
      if (result.row) {
        const category = result.row.name;
        const range = parseVoiceDateRange(raw);
        const currencies = [...new Set((accountsResponse?.accounts || []).map((row) => String(row.currency || "").toUpperCase()).filter(Boolean))];
        if (!currencies.length) currencies.push("ARS");
        const totals = await Promise.all(currencies.slice(0, 4).map(async (currency) => {
          const response = await ownerFinanceStatsApi.history({
            start: range?.from || "",
            end: range?.to || "",
            currency,
            category,
            bucket: financeHistoryBucket(range),
          });
          return { currency, amount: Number(response?.stats?.summary?.expense || 0), count: Number(response?.stats?.summary?.count || 0) };
        }));
        const nonZero = totals.filter((row) => row.amount > 0);
        const primary = nonZero.find((row) => row.currency === "ARS") || nonZero[0] || totals.find((row) => row.currency === "ARS") || totals[0];
        const params = new URLSearchParams();
        params.set("vista", "historico");
        params.set("categoria", category);
        if (primary?.currency) params.set("moneda", primary.currency);
        if (range?.from && range?.to) {
          params.set("desde", range.from);
          params.set("hasta", range.to);
        }
        navigate(`/dashboard/owner/estadisticas?${params.toString()}`);
        if (!nonZero.length) return { handled: true, message: `No encontré gastos en ${category} ${financeRangeLabel(range)}. Igual te abro Estadísticas con ese filtro para revisarlo.` };
        const spokenTotals = nonZero.map((row) => spokenMoney(row.amount, row.currency)).join(" y ");
        const movementCount = nonZero.reduce((sum, row) => sum + Number(row.count || 0), 0);
        return { handled: true, message: `En ${category} gastaste ${spokenTotals} ${financeRangeLabel(range)}${movementCount ? ` en ${movementCount} ${movementCount === 1 ? "movimiento" : "movimientos"}` : ""}. Te abro Estadísticas ya filtrado para que veas de dónde sale.` };
      }
      if (result.alternatives?.length) {
        return { handled: true, message: `Encontré categorías parecidas: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál querés revisar.` };
      }
    }

    const navigationRequest = /\b(?:abri|abrime|abrir|mostra|mostrame|mostrar|llevame|anda|ir a|ver la ficha|ver ficha)\b/.test(text);
    if (navigationRequest) {
      const wantsClient = /\b(cliente|persona)\b/.test(text);
      const wantsProject = /\b(proyecto|frente|iniciativa)\b/.test(text);
      const wantsRoutine = /\b(rutina|habito)\b/.test(text);
      const dateKey = !wantsClient && !wantsProject && !wantsRoutine ? parseVoiceDateKey(raw) : null;
      if (dateKey && /\b(?:dia|fecha|hoy|ayer|lunes|martes|miercoles|jueves|viernes|sabado|domingo|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|\d{1,2})\b/.test(text)) {
        navigate(`/dashboard/owner/calendario?fecha=${encodeURIComponent(dateKey)}`);
        return { handled: true, message: `Abro el ${spokenDate(dateKey)}.` };
      }

      const query = navigationQuery(text);
      const [clientsResponse, initiativesResponse, routinesResponse] = await Promise.all([
        ownerApi.listClients({ query, limit: 60 }),
        ownerApi.listInitiatives({ status: "", limit: 100 }),
        ownerApi.listRoutines({ includeInactive: true, limit: 150 }),
      ]);
      let candidates = [
        ...(clientsResponse?.clients || []).map((row) => ({ ...row, __kind: "client" })),
        ...(initiativesResponse?.initiatives || []).map((row) => ({ ...row, __kind: "project" })),
        ...(routinesResponse?.routines || []).map((row) => ({ ...row, __kind: "routine" })),
      ];
      if (wantsClient) candidates = candidates.filter((row) => row.__kind === "client");
      if (wantsProject) candidates = candidates.filter((row) => row.__kind === "project");
      if (wantsRoutine) candidates = candidates.filter((row) => row.__kind === "routine");
      const result = chooseEntity(candidates, query, ["name", "company", "email", "outcome", "notes"]);
      if (result.row) {
        const row = result.row;
        const id = idOf(row);
        if (row.__kind === "client") navigate(`/dashboard/owner/clientes?cliente=${encodeURIComponent(id)}`);
        if (row.__kind === "project") navigate(`/dashboard/owner/proyectos/${encodeURIComponent(id)}`);
        if (row.__kind === "routine") navigate(`/dashboard/owner/rutinas/${encodeURIComponent(id)}`);
        return { handled: true, message: `Abro ${row.name || row.company || "la ficha"}.` };
      }
      if (result.alternatives?.length) {
        return { handled: true, message: `Encontré varias fichas parecidas: ${result.alternatives.map((row) => row.name || row.company).join(", ")}. Decime cuál.` };
      }
      return { handled: true, message: "No encontré esa ficha. Decime el nombre un poco más completo." };
    }

    const range = parseVoiceDateRange(raw);
    if (range && includesAny(text, ["que hice", "que paso", "recuerdos", "recordame", "resumen de", "que estuve haciendo"])) {
      const response = await ownerApi.memories({ from: range.from, to: range.to });
      return { handled: true, message: memorySpeech(response?.memories, range) };
    }

    if (includesAny(text, ["hola jarvis", "jarvis estas ahi", "resumen del dia", "resumen de hoy", "que tengo hoy", "como viene mi dia", "que hago ahora", "por donde empiezo", "como esta mi plata", "cuanto trabaje hoy", "que sabes de mi"])) {
      const snapshot = await currentSnapshot({ force: true });
      const center = snapshot.center || {};
      const calendar = snapshot.calendar || {};
      const assistant = snapshot.assistant || {};
      const commitments = (calendar?.day?.commitments || []).filter((item) => item.status === "open");
      const routines = (calendar?.day?.routines || []).filter((item) => !item.completed);
      const timeEntries = calendar?.day?.timeEntries || [];
      const workMinutes = timeEntries.reduce((sum, item) => sum + Number(item.minutes || 0), 0);
      const currency = assistant?.currency || "ARS";
      const available = Number(center?.finance?.totalsByCurrency?.[currency] || 0);
      const outstanding = Number(center?.commercial?.outstandingByCurrency?.[currency] || 0);
      const gap = Number(assistant?.economy?.uncoveredGap || 0);

      if (includesAny(text, ["cuanto trabaje hoy"])) return { handled: true, highlightSection: "today-agenda", message: `Hoy llevás ${spokenDuration(workMinutes)} de trabajo real registrado.` };
      if (includesAny(text, ["como esta mi plata"])) return { handled: true, highlightSection: "today-money", message: `Te marco el dinero. Tenés ${spokenMoney(available, currency)} disponible.${outstanding > 0 ? ` Hay ${spokenMoney(outstanding, currency)} por cobrar.` : ""}` };
      if (includesAny(text, ["que hago ahora", "por donde empiezo"])) {
        const projectDecision = (assistant?.questions || []).find((question) => question.type === "project_daily_review");
        if (projectDecision) return { handled: true, highlightSection: "today-projects", message: `${projectDecision.text} Empecemos por ahí.` };
        const first = commitments[0];
        if (first) return { handled: true, highlightSection: "today-agenda", message: `Tus proyectos están ordenados. Ahora sigue ${first.title}.` };
        if (outstanding > 0) return { handled: true, highlightSection: "today-money", message: `No hay una obligación inmediata. Te marco lo que está por cobrar.` };
        if (routines[0]) return { handled: true, highlightSection: "today-agenda", message: `Lo próximo que ya existe es ${routines[0].name}.` };
        return { handled: true, message: "No hay una obligación inmediata. El bloque puede quedar libre; no hace falta crear una tarea para ocuparlo." };
      }
      if (includesAny(text, ["que sabes de mi"])) {
        return { handled: true, message: `Estoy conectado a tu agenda, movimientos, clientes, proyectos, rutinas, trabajo real y recuerdos de Owner 360. Hoy veo ${commitments.length} tareas abiertas, ${routines.length} rutinas pendientes y ${spokenDuration(workMinutes)} registrados.` };
      }
      const projectReviews = Number(assistant?.stats?.projectsPendingDailyReview || 0);
      return { handled: true, highlightSection: projectReviews > 0 ? "today-projects" : commitments.length || routines.length ? "today-agenda" : "today-money", message: projectReviews > 0 ? "Hay proyectos que necesitan una decisión. Te marco el primero." : commitments.length || routines.length ? "Los proyectos están ordenados. Te marco lo que sigue hoy." : "Todo está bastante despejado. No hace falta inventar trabajo." };
    }

    if (includesAny(text, ["que tengo en stock", "stock personal", "que comida tengo", "que alimentos tengo"])) {
      const response = await ownerApi.listInventoryItems({ limit: 20 });
      const items = response?.items || [];
      if (!items.length) return { handled: true, message: "Todavía no tenés stock personal cargado." };
      const summary = items.slice(0, 8).map((item) => `${item.name}: ${Number(item.quantity || 0).toLocaleString("es-AR")} ${item.unit || "unidades"}`).join(", ");
      return { handled: true, message: `En stock tengo registrado: ${summary}.` };
    }

    return { handled: false };
  }, [currentSnapshot, navigate]);

  return (
    <OwnerVoiceOperatorProvider
      actions={voiceActions}
      introMessage={briefing}
      onUnhandledUtterance={handleUnhandled}
      alwaysOn
      routeContext={location.pathname}
      loadNavigationLearning={ownerApi.navigationLearning}
      recordNavigationLearning={ownerApi.recordNavigationLearning}
    >
      <Box
        sx={{
          width: "100%",
          height: { xs: "auto", md: "calc(100dvh - 68px)" },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: { xs: "visible", md: "hidden" },
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: { xs: "calc(56px + env(safe-area-inset-top))", sm: "calc(64px + env(safe-area-inset-top))", md: 0 },
            zIndex: 1080,
            flex: "0 0 auto",
            minWidth: 0,
            px: { xs: .45, sm: .65 },
            py: .2,
            bgcolor: "rgba(var(--wp-panel-surface-rgb, 255,255,255),.97)",
            borderBottom: "1px solid",
            borderColor: "divider",
            backdropFilter: "blur(12px)",
          }}
        >
          <OwnerVoiceOperatorPanel persistent pathname={location.pathname} />
        </Box>

        <Box sx={{ display: "flex", flex: "1 1 auto", minHeight: 0, minWidth: 0 }}>
          <OwnerWorkspaceNavigation />
          <Box
            component="section"
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: 0,
              overflow: { xs: "visible", md: "auto" },
              overscrollBehavior: "contain",
              px: { xs: .55, sm: .9, md: 1.15 },
              py: { xs: .55, md: .9 },
              "& > *": { maxWidth: "none !important", mx: "0 !important" },
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </OwnerVoiceOperatorProvider>
  );
}
